import { type SelectionEvent } from '../events/types';
import { type SelectionState } from '../state/types';

import { deselectAllReducer } from './deselect-all';
import { extendToCellReducer } from './extend-to-cell';
import { extendToColumnReducer } from './extend-to-column';
import { extendToRowReducer } from './extend-to-row';
import { fillApplyReducer } from './fill-apply';
import { fillDragReducer } from './fill-drag';
import { fillStartReducer } from './fill-start';
import { focusCellReducer } from './focus-cell';
import { previewSelectionActionReducer } from './preview-selection-action';
import { reconcileSelectionsReducer } from './reconcile-selections';
import { requestClearCellsReducer } from './request-clear-cells';
import { requestCellEditReducer } from './request-edit-cell';
import { selectAllReducer } from './select-all';
import { selectCellReducer } from './select-cell';
import { selectCellRangeReducer } from './select-cell-range';
import { selectColumnsReducer } from './select-columns';
import { selectRowsReducer } from './select-rows';
import { stopEditingReducer } from './stop-editing-cell';
import { toggleReducer } from './toggle';
import { type Reducer } from './types';

/**
 * Main reducer that routes events to their specific reducers. Add new reducers
 * here as you create them. Returns noop for unhandled events.
 */
export const selectionsReducer: Reducer = (
  prev: SelectionState,
  event: SelectionEvent
) => {
  if (event.type === 'reconcileSelections') {
    return reconcileSelectionsReducer(prev, event);
  }

  /**
   * If we are editing, the ONLY event we allow to process is 'stopEditing'
   * Everything else (clicks, API calls, keyboard nav) is strictly ignored (no-op).
   */
  if (prev.isEditing && event.type !== 'stopEditing') {
    return {
      next: prev,
      delta: {
        kind: 'noop',
      },
    };
  }

  if (event.type === 'selectCell') {
    return selectCellReducer(prev, event);
  }

  if (event.type === 'selectCellRange') {
    return selectCellRangeReducer(prev, event);
  }

  if (event.type === 'startEditing') {
    return requestCellEditReducer(prev, event);
  }

  if (event.type === 'stopEditing') {
    return stopEditingReducer(prev, event);
  }

  if (event.type === 'extendToCell') {
    return extendToCellReducer(prev, event);
  }

  if (event.type === 'selectRows') {
    return selectRowsReducer(prev, event);
  }

  if (event.type === 'extendToRow') {
    return extendToRowReducer(prev, event);
  }

  if (event.type === 'extendToColumn') {
    return extendToColumnReducer(prev, event);
  }

  if (event.type === 'toggleRow') {
    return toggleReducer(prev, {
      axis: 'row',
      primaryCoord: event.row,
      source: event.source,
    });
  }

  if (event.type === 'selectColumns') {
    return selectColumnsReducer(prev, event);
  }

  if (event.type === 'toggleColumn') {
    return toggleReducer(prev, {
      axis: 'col',
      primaryCoord: event.col,
      source: event.source,
    });
  }

  if (event.type === 'deselectAll') {
    return deselectAllReducer(prev, event);
  }

  if (event.type === 'selectAll') {
    return selectAllReducer(prev, event);
  }

  if (event.type === 'previewSelectionAction') {
    return previewSelectionActionReducer(prev, event);
  }

  if (event.type === 'focusCell') {
    return focusCellReducer(prev, event);
  }

  if (event.type === 'fillStart') {
    return fillStartReducer(prev, event);
  }

  if (event.type === 'fillDrag') {
    return fillDragReducer(prev, event);
  }
  if (event.type === 'fillApply') {
    return fillApplyReducer(prev, event);
  }

  if (event.type === 'clearCells') {
    return requestClearCellsReducer(prev, event);
  }

  return { next: prev, delta: { kind: 'noop' } };
};
